<script lang="ts">
    import { getContainer } from '$components/Context/ContainerContext.js';
    import FormErrors from '$components/Form/FormErrors.svelte';
    import HtmlForm from '$components/Form/HtmlForm.svelte';
    import Row from '$components/Form/Row.svelte';
    import Textarea from '$components/Form/Textarea.svelte';
    import {
        createForm,
        createOnSubmit,
        getCategoryStrategyForCategoryId,
        type MultiQuoteOnSubmitSuccessParams,
    } from './MultiQuoteFormData';
    import { cx } from '$lib/utils/cx';
    import SelectCountry from '$components/Form/SelectCountry/SelectCountry.svelte';
    import SelectCityId from '$components/Form/SelectCity/SelectCityId.svelte';
    import Input from '$components/Form/Input.svelte';
    import * as t from '$trans';
    import BBCode from '$components/BBCode/BBCode.svelte';
    import SelectCategoryInHierarchy from '$components/Form/SelectCategoryInHierarchy/SelectCategoryInHierarchy.svelte';
    import { fly } from 'svelte/transition';
    import { quadOut } from 'svelte/easing';
    import { CountryUseCurrent } from '$lib/UrlHelper';
    import { escapeAttrValue } from '$lib/bbcode/parser/helpers.js';
    import { tick } from 'svelte';

    let initialCategoryId: number | null = null;
    export { initialCategoryId as categoryId };

    export let withCategory: boolean;

    export let relatedCmsPostId: number | null = null;
    let initialCountry: string | null = null;
    export { initialCountry as country };

    export let htmlClass = '';
    export let successClass = 'alert alert-success';
    export let errorClass = 'bg-white p-8 rounded-md border text-pretty';

    const { currentUserStore, urlHelper, lang } = getContainer();

    const currentUserId = currentUserStore().currentUserId();

    let [form, fields] = createForm('multi-quote-form', {
        user: $currentUserId,
        categoryId: initialCategoryId,
        relatedCmsPostId,
        country: initialCountry?.toUpperCase() ?? null,
        message: ``,
    });

    $: country = form.stores().fieldValue(fields.country);
    $: categoryId = form.stores().fieldValue(fields.categoryId);

    $: strategy = getCategoryStrategyForCategoryId($categoryId);
    $: ExtraComponentForm = strategy?.extraComponentForm ?? null;

    $: onSubmit = createOnSubmit(getContainer());

    let successMessage: string | null = null;
    let errorMessage: string | null = null;

    let successOrErrorDiv: HTMLDivElement | undefined;

    function onSubmitSuccess({ form, data }: MultiQuoteOnSubmitSuccessParams) {
        form.dispatch((_type) => form.getInitialState());
        if (data.created != null) {
            if (data.created.userJustCreated === true) {
                successMessage = t.multiquote.alert__created_with_registration({}, $lang);
            } else {
                successMessage = t.multiquote.alert__created({}, $lang);
            }
        } else {
            if (data.isJobRequest === false) {
                errorMessage = t.multiquote.alert__error_unknown({}, $lang);
            } else {
                errorMessage = t.multiquote.alert__error_is_job_request(
                    {
                        urlRegistration: escapeAttrValue(urlHelper().registration()),
                        urlSearchJobs: escapeAttrValue(
                            urlHelper().searchJobs({
                                _country: $country ?? CountryUseCurrent,
                            }),
                        ),
                        urlSearchUsers: escapeAttrValue(
                            urlHelper().searchUsers({
                                _country: $country ?? CountryUseCurrent,
                            }),
                        ),
                    },
                    $lang,
                );
            }
        }
        tick().then(() => {
            successOrErrorDiv?.scrollIntoView({ behavior: 'smooth' });
        });
    }
</script>

{#if successMessage != null}
    <div bind:this={successOrErrorDiv} class={successClass}>
        <BBCode bbcode={successMessage} />
    </div>
{:else if errorMessage != null}
    <div bind:this={successOrErrorDiv} class={errorClass}>
        <BBCode bbcode={errorMessage} />
    </div>
{:else}
    <HtmlForm class={cx(htmlClass)} {form} {onSubmit} {onSubmitSuccess}>
        <div class="flex flex-col gap-8">
            {#if withCategory}
                <Row
                    {form}
                    field={fields.categoryId}
                    class=""
                    let:field
                    let:id
                    label={`Secteur d'activité`}
                >
                    <SelectCategoryInHierarchy
                        {form}
                        {field}
                        validation="force-leaf"
                        parentId={initialCategoryId ?? null}
                        {id}
                    />
                </Row>
            {/if}

            {#if $currentUserId == null}
                <div class="grid grid-cols-1 t:grid-cols-2 gap-8">
                    <Row
                        class=""
                        {form}
                        field={fields.firstname}
                        label={'Prénom'}
                        required
                        let:field
                    >
                        <Input
                            {form}
                            {field}
                            toText={(id) => id}
                            fromText={(id) => id}
                            class={''}
                        />
                    </Row>

                    <Row
                        class=""
                        {form}
                        field={fields.lastname}
                        label={'Nom'}
                        required
                        let:field
                    >
                        <Input
                            {form}
                            {field}
                            toText={(id) => id}
                            fromText={(id) => id}
                            class={''}
                        />
                    </Row>
                </div>

                <Row
                    class=""
                    {form}
                    field={fields.email}
                    label={'Email'}
                    required
                    let:field
                >
                    <Input
                        {form}
                        {field}
                        type="email"
                        toText={(id) => id}
                        fromText={(id) => id}
                        class={''}
                    />
                </Row>
            {/if}

            <Row class="" {form} field={fields.phone} label={'Telephone'} let:field>
                <Input
                    {form}
                    {field}
                    toText={(id) => id}
                    fromText={(id) => id}
                    class={''}
                />
            </Row>

            <div class={'grid grid-cols-1 t:grid-cols-2 gap-8'}>
                <Row {form} class="" field={fields.country} label="Pays" let:field>
                    <SelectCountry required={false} {form} {field} placeholder="Pays" />
                </Row>

                {#if $country != null}
                    <div
                        in:fly={{
                            duration: 150,
                            easing: quadOut,
                            x: '-10px',
                            opacity: 0,
                        }}
                    >
                        <Row {form} class="" field={fields.city} label="Ville" let:field>
                            <SelectCityId {form} {field} country={$country} />
                        </Row>
                    </div>
                {/if}
            </div>

            {#if ExtraComponentForm != null}
                <svelte:component this={ExtraComponentForm} {form} {fields} />
            {:else}
                <!--Précisez votre besoin, soyez le plus précis dans votre demande de
                devis-->

                <Row
                    {form}
                    field={fields.message}
                    label={'Votre demande'}
                    required
                    let:field
                >
                    <p>
                        Soyez le plus précis dans votre demande de devis : biens ou
                        services recherchés, les quantités, les délais de livraison…
                    </p>

                    <Textarea
                        {form}
                        {field}
                        placeholder={`Soyez le plus précis dans votre demande de devis : biens ou services recherchés, les quantités, les délais de livraison…`}
                        toText={(id) => id}
                        fromText={(id) => id}
                        class={''}
                        rows={5}
                    />
                </Row>
            {/if}
        </div>

        <div
            class="mt-8 bg-red-100 rounded-[4px] border border-red-400 text-red-800 p-4 shadow-leo my-4 flex flex-col gap-2"
        >
            <p class="m-0">
                Les demandes d'emploi ne sont pas autorisées sur ce formulaire. Nous ne
                les validerons pas. Si vous recherchez un emploi, merci de
                <a
                    href={urlHelper().searchJobs({
                        _country: $country ?? CountryUseCurrent,
                    })}
                    class="text-link hover:underline inline">cliquer-ici.</a
                >
            </p>
        </div>

        <div class="flex flex-col gap-8">
            <FormErrors {form} displayIfHasFieldsErrors />
        </div>

        <slot {form} />
    </HtmlForm>
{/if}
